import { createElement, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { addQueryArgs } from "@wordpress/url";
import { fetchWithTokenRefresh } from "@hooks/shared/fetch-with-token-refresh";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { useUserContext } from "@hooks/use-auth-context";
import { useRefreshTokenMutation } from "@hooks/use-refresh-token-mutation";

const ENDPOINT = "wp-json/smassets/v1/check-unique";

type CheckUniqueType = "asset" | "ticket" | "user";

export type UniqueCheckField = { value: string; source: string; field?: string };
export type UniqueCheckResults = Record<string, { isUnique: boolean | null; isChecking: boolean }>;

/**
 * Checks uniqueness for multiple fields simultaneously, debouncing each independently.
 * Pass only fields that should be checked (i.e. unique: true in config).
 */
export function useCheckUniqueFields({
  type,
  uniqueFields,
  excludeId,
}: {
  type: CheckUniqueType;
  uniqueFields: Record<string, UniqueCheckField>;
  excludeId?: string | number;
}): UniqueCheckResults {
  const [results, setResults] = useState<UniqueCheckResults>({});
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const prevFieldsRef = useRef<Record<string, string>>({});

  const { getApiUrl } = useMultiSiteContext();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();
  const url = useMemo(() => getApiUrl(ENDPOINT), [getApiUrl]);

  const stableRef = useRef({ type, url, user, refreshUserToken, excludeId });
  stableRef.current = { type, url, user, refreshUserToken, excludeId };

  useEffect(() => {
    const timers = timersRef.current;
    return () => { Object.values(timers).forEach(clearTimeout); };
  }, []);

  useEffect(() => {
    const prevFields = prevFieldsRef.current;

    Object.entries(uniqueFields).forEach(([resultKey, fieldData]) => {
      if (prevFields[resultKey] === fieldData.value) return;

      if (timersRef.current[resultKey]) clearTimeout(timersRef.current[resultKey]);

      if (!fieldData.value.trim()) {
        setResults((prev) => ({ ...prev, [resultKey]: { isUnique: null, isChecking: false } }));
        return;
      }

      setResults((prev) => ({ ...prev, [resultKey]: { isUnique: null, isChecking: true } }));

      timersRef.current[resultKey] = setTimeout(async () => {
        const { type: t, url: u, user: usr, refreshUserToken: refresh, excludeId: eid } = stableRef.current;
        if (!u || typeof u !== "string") return;
        try {
          const params: Record<string, string> = { type: t, source: fieldData.source, value: fieldData.value };
          if (fieldData.field) params.field = fieldData.field;
          if (eid != null) params.exclude_id = String(eid);

          const response = await fetchWithTokenRefresh(
            addQueryArgs(u, params),
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${usr?.authToken ?? ""}`,
                "Content-Type": "application/json",
              },
            },
            () => refresh(usr?.refreshToken ?? ""),
          );

          if (!response.ok) {
            setResults((prev) => ({ ...prev, [resultKey]: { isUnique: null, isChecking: false } }));
            return;
          }
          const data = await response.json();
          setResults((prev) => ({ ...prev, [resultKey]: { isUnique: !!data.unique, isChecking: false } }));
        } catch {
          setResults((prev) => ({ ...prev, [resultKey]: { isUnique: null, isChecking: false } }));
        }
      }, 500);
    });

    prevFieldsRef.current = Object.fromEntries(Object.entries(uniqueFields).map(([k, f]) => [k, f.value]));
    // uniqueFields reference is stabilised by useMemo in the consumer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueFields]);

  return results;
}

/** Checks uniqueness for a single field with debounce. Fires on every value change. */
export function useCheckUnique({
  type,
  source,
  field,
  value,
  excludeId,
}: {
  type: CheckUniqueType;
  source: string;
  field?: string;
  value: string;
  excludeId?: string | number;
}): { isUnique: boolean | null; isChecking: boolean } {
  const [isUnique, setIsUnique] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { getApiUrl } = useMultiSiteContext();
  const { user } = useUserContext();
  const { refreshUserToken } = useRefreshTokenMutation();
  const url = useMemo(() => getApiUrl(ENDPOINT), [getApiUrl]);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!value.trim()) {
      setIsUnique(null);
      setIsChecking(false);
      return;
    }

    setIsChecking(true);
    setIsUnique(null);

    timerRef.current = setTimeout(async () => {
      if (!url || typeof url !== "string") { setIsChecking(false); return; }
      try {
        const params: Record<string, string> = { type, source, value };
        if (field) params.field = field;
        if (excludeId != null) params.exclude_id = String(excludeId);

        const response = await fetchWithTokenRefresh(
          addQueryArgs(url, params),
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${user?.authToken ?? ""}`,
              "Content-Type": "application/json",
            },
          },
          () => refreshUserToken(user?.refreshToken ?? ""),
        );

        if (!response.ok) { setIsUnique(null); return; }
        const data = await response.json();
        setIsUnique(!!data.unique);
      } catch {
        setIsUnique(null);
      } finally {
        setIsChecking(false);
      }
    }, 500);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, type, source, field, excludeId, url, user, refreshUserToken]);

  return { isUnique, isChecking };
}

export function getUniquenessHelperText(
  isChecking: boolean,
  isUnique: boolean | null,
  value: string,
): ReactNode | undefined {
  if (!value.trim()) return undefined;
  if (isChecking) return "Checking availability...";
  if (isUnique === true) return "✓ This value is available";
  if (isUnique === false) {
    return createElement(
      "span",
      { style: { color: "#ed6c02" } },
      "⚠ This value is already in use",
    );
  }
  return undefined;
}
